'use client';

import {
  Box,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  KafkaSaslMechanism,
  KafkaSecurityProtocol,
  Provider,
} from '@repo/api-gw-sdk';

import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';

import type { IngestionPipelineWizardState } from '../IngestionPipelineWizardState';
import { getStepperLabels } from '../kafkaHelpers';

const SECURITY_PROTOCOL_OPTIONS = [
  {
    value: KafkaSecurityProtocol.KAFKA_SECURITY_PROTOCOL_PLAINTEXT,
    label: 'PLAINTEXT',
  },
  { value: KafkaSecurityProtocol.KAFKA_SECURITY_PROTOCOL_SSL, label: 'SSL' },
  {
    value: KafkaSecurityProtocol.KAFKA_SECURITY_PROTOCOL_SASL_PLAINTEXT,
    label: 'SASL_PLAINTEXT',
  },
  {
    value: KafkaSecurityProtocol.KAFKA_SECURITY_PROTOCOL_SASL_SSL,
    label: 'SASL_SSL',
  },
];

const SASL_MECHANISM_OPTIONS = [
  { value: KafkaSaslMechanism.KAFKA_SASL_MECHANISM_PLAIN, label: 'PLAIN' },
  {
    value: KafkaSaslMechanism.KAFKA_SASL_MECHANISM_SCRAM_SHA_256,
    label: 'SCRAM-SHA-256',
  },
  {
    value: KafkaSaslMechanism.KAFKA_SASL_MECHANISM_SCRAM_SHA_512,
    label: 'SCRAM-SHA-512',
  },
  {
    value: KafkaSaslMechanism.KAFKA_SASL_MECHANISM_OAUTHBEARER,
    label: 'OAUTHBEARER',
  },
  { value: KafkaSaslMechanism.KAFKA_SASL_MECHANISM_GSSAPI, label: 'GSSAPI' },
];

const { validation: ARN_VALIDATION_REQUIRED, placeholder: ARN_PLACEHOLDER } =
  getCloudFieldConfig(Provider.Aws, 'arn', { required: true });

const { validation: ARN_VALIDATION_OPTIONAL } = getCloudFieldConfig(
  Provider.Aws,
  'arn'
);

export function KafkaSecurityStep(
  props: StepProps<IngestionPipelineWizardState>
) {
  const {
    kafkaSecurityProtocol,
    kafkaSaslMechanism,
    kafkaCredentialsSecretArn,
    kafkaTlsSecretArn,
  } = props.wizardState;

  const isSaslProtocol = (protocol: KafkaSecurityProtocol) =>
    protocol === KafkaSecurityProtocol.KAFKA_SECURITY_PROTOCOL_SASL_PLAINTEXT ||
    protocol === KafkaSecurityProtocol.KAFKA_SECURITY_PROTOCOL_SASL_SSL;

  const isSasl = isSaslProtocol(kafkaSecurityProtocol);

  const credentialsDisabled = !isSasl;

  const canGoNext = () => {
    if (kafkaSecurityProtocol.trim() === '') return false;
    if (isSasl && kafkaSaslMechanism.trim() === '') return false;
    if (
      isSasl &&
      !ARN_VALIDATION_REQUIRED.validateInput(kafkaCredentialsSecretArn)
    )
      return false;
    if (
      kafkaTlsSecretArn.trim() !== '' &&
      !ARN_VALIDATION_OPTIONAL.validateInput(kafkaTlsSecretArn)
    )
      return false;
    return true;
  };

  return (
    <StepContainer
      stepperIndex={2}
      stepperLabels={getStepperLabels(props.wizardState.sourceType)}
      canGoNext={canGoNext}
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      onBackClick={props.back}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)'>
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Authentication</Typography>
              <Typography variant='body2' color='text.secondary'>
                Configure the security protocol and credentials for the Kafka
                connection.
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <FormLabel>Security Protocol</FormLabel>
              <Select
                size='small'
                value={kafkaSecurityProtocol}
                onChange={(e) => {
                  const protocol = SECURITY_PROTOCOL_OPTIONS.find(
                    (o) => o.value === e.target.value
                  );
                  if (!protocol) return;
                  const sasl = isSaslProtocol(protocol.value);
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaSecurityProtocol: protocol.value,
                    kafkaSaslMechanism: sasl
                      ? KafkaSaslMechanism.KAFKA_SASL_MECHANISM_PLAIN
                      : '',
                    ...(!sasl && { kafkaCredentialsSecretArn: '' }),
                  }));
                }}
                data-testid='kafka-security-protocol-select'
              >
                {SECURITY_PROTOCOL_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isSasl && (
              <FormControl fullWidth>
                <FormLabel>SASL Mechanism</FormLabel>
                <Select
                  size='small'
                  value={
                    kafkaSaslMechanism ||
                    KafkaSaslMechanism.KAFKA_SASL_MECHANISM_PLAIN
                  }
                  onChange={(e) =>
                    props.setWizardState((s) => ({
                      ...s,
                      kafkaSaslMechanism: e.target.value,
                    }))
                  }
                  data-testid='kafka-sasl-mechanism-select'
                >
                  {SASL_MECHANISM_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {credentialsDisabled ? (
              <FormControl fullWidth disabled>
                <FormLabel>
                  <Typography variant='subtitle1' color='inherit'>
                    Credentials Secret ARN
                  </Typography>
                </FormLabel>
                <TextField
                  size='medium'
                  disabled
                  value=''
                  placeholder={ARN_PLACEHOLDER}
                  slotProps={{
                    htmlInput: {
                      'data-testid': 'kafka-credentials-secret-arn-input',
                    },
                  }}
                />
              </FormControl>
            ) : (
              <FormTextField
                title='Credentials Secret ARN'
                placeholder={ARN_PLACEHOLDER}
                value={kafkaCredentialsSecretArn}
                validation={ARN_VALIDATION_REQUIRED}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaCredentialsSecretArn: e.target.value,
                  }))
                }
                dataTestId='kafka-credentials-secret-arn-input'
              />
            )}

            <FormTextField
              title='TLS Secret ARN (Optional)'
              placeholder={ARN_PLACEHOLDER}
              value={kafkaTlsSecretArn}
              validation={ARN_VALIDATION_OPTIONAL}
              onChange={(e) =>
                props.setWizardState((s) => ({
                  ...s,
                  kafkaTlsSecretArn: e.target.value,
                }))
              }
              dataTestId='kafka-tls-secret-arn-input'
            />
          </Stack>
        </Box>
      </Stack>
    </StepContainer>
  );
}
